/**
 * RemoveFromPipelineDialogContainer — the confirmation gate in front of a
 * pipeline-card removal (SE-5500).
 *
 * Renders the shared `ConfirmDialog` molecule rather than a bespoke
 * dialog: the destructive chrome, the close-while-in-flight guard, and the
 * key-reset that clears fields between openings are all already solved there.
 * This container adds only what is specific to removal:
 *
 * - the copy, including the explicit "the player record and watchlists are
 *   untouched" line that states AC2's scope to the person confirming;
 * - the SUBMIT guard. `onConfirm` is async, and `setState` commits
 *   asynchronously, so two Confirm presses landing in the same tick would both
 *   read a stale `isRemoving === false` and fire two removals. The ref is the
 *   correctness guard; the state is what re-renders the button (the same
 *   pattern the drawer's stage picker and SE-5504's `loadMore` use);
 * - the FAILURE path. A failed removal keeps the dialog OPEN and surfaces the
 *   error inside it. Because the shared dialog only resets its fields when
 *   `isOpen` flips, the reason the user typed survives the retry — retyping a
 *   required justification after a network blip is how you train people to
 *   write throwaway reasons.
 * @module features/player-kanban/components/molecules/RemoveFromPipelineDialog/RemoveFromPipelineDialogContainer
 */

import { Trash2 } from "lucide-react-native";
import { useCallback, useRef, useState } from "react";

import ConfirmDialog from "@/components/molecules/ConfirmDialog";

import { TEST_ID_PLAYER_PREVIEW_DRAWER } from "e2e/testId";

/**
 * Cap on the recorded reason. Mirrors the server's limit exactly: an over-long
 * rationale is REJECTED there rather than truncated (a truncated reason is a
 * CHANGED reason on an append-only audit record), so the field stops accepting
 * input at the cap and the dialog's counter shows where the cap is.
 */
export const REMOVE_REASON_MAX_LENGTH = 1000;

/** Copy shown when the removal mutation fails; the typed reason is retained. */
export const REMOVE_FAILURE_MESSAGE =
  "Couldn't remove this player. Your reason has been kept — try again.";

/** Props for {@link RemoveFromPipelineDialogContainer}. */
export interface RemoveFromPipelineDialogProps {
  /** Whether the dialog is visible. */
  readonly isOpen: boolean;
  /** Player name for the copy; falls back to "this player" when unknown. */
  readonly playerName?: string;
  /** Whether the board is a PIPELINE board (pipeline vs board wording). */
  readonly isPipelineBoard: boolean;
  /** Dismiss without removing. */
  readonly onCancel: () => void;
  /**
   * Performs the removal. MUST reject when the removal failed — a resolved
   * promise is treated as success and closes the flow.
   */
  readonly onConfirm: (rationale: string) => Promise<void>;
  /** Invoked after a successful removal, so the caller can close the drawer. */
  readonly onRemoved: () => void;
}

/**
 * Confirmation dialog for removing a player's card from a pipeline/board.
 * @param props - {@link RemoveFromPipelineDialogProps}
 * @param props.isOpen - Whether the dialog is visible
 * @param props.playerName - Player name for the copy
 * @param props.isPipelineBoard - Whether the board is a PIPELINE board
 * @param props.onCancel - Dismiss without removing
 * @param props.onConfirm - Performs the removal (rejects on failure)
 * @param props.onRemoved - Success callback (closes the drawer)
 * @returns The configured {@link ConfirmDialog}.
 */
const RemoveFromPipelineDialogContainer = ({
  isOpen,
  playerName,
  isPipelineBoard,
  onCancel,
  onConfirm,
  onRemoved,
}: RemoveFromPipelineDialogProps) => {
  const [isRemoving, setIsRemoving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const isRemovingRef = useRef(false);

  const surface = isPipelineBoard ? "pipeline" : "board";
  const who = playerName ?? "this player";

  const handleConfirm = useCallback(
    (rationale?: string) => {
      // A double-clicked Confirm lands twice before `setIsRemoving(true)`
      // renders, so the ref — not the state — is what makes this idempotent.
      if (isRemovingRef.current) return;
      // The dialog gates confirm on a non-blank reason; this is the belt to
      // that braces, so no code path can send an empty rationale.
      const reason = rationale?.trim() ?? "";
      if (!reason) return;

      isRemovingRef.current = true;
      setIsRemoving(true);
      setErrorMessage(undefined);

      // The catch is attached to `onConfirm` DIRECTLY rather than chained after
      // the success handler. Chained, it would also swallow anything
      // `onRemoved()` throws — reporting a removal that actually succeeded as a
      // failure, and leaving the dialog open over a card that is already gone.
      onConfirm(reason)
        .then(
          () => {
            // Contained deliberately. The removal has already SUCCEEDED by this
            // point, so a throw from the caller's teardown must not be reported
            // as a failed removal — but nor should it escape as an unhandled
            // rejection. Swallowing it here is the narrowest correct behaviour:
            // the card is gone, and the dialog closes as it should.
            try {
              onRemoved();
            } catch {
              // Intentionally ignored — see above.
            }
          },
          () => {
            // Stay OPEN: the dialog's fields only reset on an isOpen flip, so
            // the typed reason is still there for the retry.
            setErrorMessage(REMOVE_FAILURE_MESSAGE);
          }
        )
        .finally(() => {
          isRemovingRef.current = false;
          setIsRemoving(false);
        });
    },
    [onConfirm, onRemoved]
  );

  const handleCancel = useCallback(() => {
    if (isRemovingRef.current) return;
    setErrorMessage(undefined);
    onCancel();
  }, [onCancel]);

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={handleCancel}
      onConfirm={handleConfirm}
      title={`Remove from ${surface}`}
      body={[
        `Remove ${who} from this ${surface}?`,
        `Only the ${surface} card is deleted — the player's record, notes and watchlist memberships are untouched.`,
        "A reason is required and is recorded on the activity log.",
      ]}
      icon={Trash2}
      tone="danger"
      confirmLabel={isRemoving ? "Removing…" : "Remove"}
      isConfirming={isRemoving}
      requireReason={{
        accessibilityLabel: "Reason for removing this player",
        placeholder: `Why is ${who} being removed from this ${surface}?`,
        maxLength: REMOVE_REASON_MAX_LENGTH,
        helperText: "Required — recorded on the activity log",
      }}
      errorMessage={errorMessage}
      testID={TEST_ID_PLAYER_PREVIEW_DRAWER.REMOVE_DIALOG}
    />
  );
};

RemoveFromPipelineDialogContainer.displayName =
  "RemoveFromPipelineDialogContainer";

export default RemoveFromPipelineDialogContainer;
